import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";

/**
 * Renders a chatbot answer as Markdown (shared by the chatbot chat page and the general chat page).
 *
 * The answer bubble is dark purple with white text in light mode and light gray with dark purple text in dark
 * mode, so the table colors below are derived from the bubble text color (white / text-dark) instead of fixed
 * grays: the header row, borders and cells stay readable in both themes. Column alignment from the Markdown
 * separator row (:--- / ---: / :---:) is passed through, so numeric columns can be right-aligned.
 */
export const markdownComponents = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold text-blue-700 dark:text-blue-300 mb-3 mt-4">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-semibold text-blue-600 dark:text-blue-400 mb-2 mt-3">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-lg font-medium text-blue-500 dark:text-blue-400 mb-2 mt-2">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="text-sm mb-2 text-white dark:text-text-dark leading-relaxed">
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-inside space-y-1 ml-4 mb-2">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-inside space-y-1 ml-4 mb-2">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-sm text-white dark:text-text-dark">{children}</li>
  ),
  code: ({ inline, children }) => (
    <code className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1 py-0.5 rounded font-mono">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded-md overflow-auto text-xs font-mono mb-2">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-blue-300 dark:border-blue-600 pl-4 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-md mb-2 italic">
      {children}
    </blockquote>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-blue-500 dark:text-blue-400 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-3 mt-1">
      <table className="w-full border-collapse text-sm text-white dark:text-text-dark border border-white/30 dark:border-text-dark/30">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-white/15 dark:bg-text-dark/10">{children}</thead>
  ),
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => (
    <tr className="border-b border-white/20 dark:border-text-dark/20 last:border-b-0">
      {children}
    </tr>
  ),
  th: ({ children, style }) => (
    <th
      style={style}
      className="px-3 py-2 text-left font-semibold whitespace-nowrap text-white dark:text-text-dark"
    >
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td
      style={style}
      className="px-3 py-2 align-top text-white dark:text-text-dark"
    >
      {children}
    </td>
  ),
  hr: () => <hr className="my-3 border-gray-300 dark:border-gray-600" />,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
};

const MarkdownMessage = ({ text }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    rehypePlugins={[rehypeHighlight, rehypeRaw]}
    components={markdownComponents}
  >
    {text}
  </ReactMarkdown>
);

export default MarkdownMessage;
